/*******************************************************************************
Mechanical harvest acreage calculations — pure, no imports.

Kept free of browser globals (harvest_utils.js reads window.Materialize at load)
so these can be tested under plain Node: `node --test test/*.test.mjs` from Client/.
harvest_utils.js re-exports acresCompleted/acresBalance for existing callers.
*******************************************************************************/

/* Calculates the acres completed based on tree count input */
export function acresCompleted(trees, treesPerAcre) {
  if (!(trees && treesPerAcre)) return 0.00;
  return (Number(trees) / Number(treesPerAcre)).toFixed(2);
}
/* Remaining acres in a field after acresCompleted (cumulative) is subtracted */
export function acresBalance(acresCompleted, acresRemaining) {
  if (!(acresCompleted && acresRemaining)) return 0.00;
  return (Number(acresRemaining) - Number(acresCompleted)).toFixed(2);
}
/**
* batchAcresBalances(): acres balance for each record of one submission
* @param {Array} harvest: records being submitted together, in entry order
* @param {Array} fields: field rows (field, treePerAcre, mechHarvest)
* @param {Array} pHarvest: [{ field, previousHarvest: [saved records] }] per field
* @returns {Array} acresBalance per record, same order as harvest
*
* Each record's balance is a running balance in entry order: saved acres for the
* field, plus every EARLIER record in this batch on the same field, plus its own.
* Before 2026-10-09 the batch siblings were omitted, so every record after the
* first on a field was over-stated by their acres (ticket: balance only right
* after re-opening the record in Manage > Edit), and siblings could jointly
* exceed the field's acres without tripping the < 0 check in createMechHarvest2.
**/
export function batchAcresBalances(harvest, fields, pHarvest) {
  const batchAcres = {}; // field -> acres from earlier records in this batch

  return harvest.map((h) => {
    const field = fields.find((f) => f.field === h.field);
    const prevHarvest = pHarvest.find((p) => p.field === h.field);

    const preparedAcres = acresCompleted(h.treesCovered, field.treePerAcre);
    const existingAcres = prevHarvest.previousHarvest
                            .reduce((acc, cur) => acc + cur.acresCompleted, 0);
    const priorInBatch = batchAcres[h.field] || 0;
    batchAcres[h.field] = priorInBatch + Number(preparedAcres);

    const totalAcresComplete = Number(preparedAcres) + existingAcres + priorInBatch;

    return acresBalance(totalAcresComplete, field.mechHarvest);
  });
}

/* Locations without a season row of their own harvest under another's.
   75 (Naalehu) -> 71 (Kau): owner decision 2026-10-09. Mirrored server-side in
   Server/services/mech_harvest_season.js — change both together. */
export const SEASON_LOCATION = { '75': '71' };

/**
* seasonForLocation(): the current harvest season for a location
* @param {Array} seasons: current seasons as served by /fetchAllMechHarvest (selectCurrentSeasons)
* @param {String} location: '71' | '72' | '75'
* @returns {Object|undefined} season row; undefined when none matches
*
* Matches on season.location (varchar(3)). Until 2026-10-09 the create path was
* positional — seasons[0] for 71, seasons[1] for 72 — but the server lists newest
* first and the 71 row is created before the 72 row each year, so the list
* arrives [72, 71] and every Kau record was filed under the Keaau season and vice
* versa (5,542 of 5,703 rows in seasons 13..18). Editing re-filed a record under
* the correct season, so one field/round's records ended up split across two
* seasonIds. No match now returns undefined so the caller refuses, instead of
* guessing another location's season.
**/
export function seasonForLocation(seasons, location) {
  const seasonLocation = SEASON_LOCATION[String(location)] || String(location);
  return seasons.find((s) => String(s.location) === seasonLocation);
}



// WEBPACK FOOTER //
// ./src/mech_harvest_calc.js